import CarrierRulesMissing from '../carrier/CarrierRulesMissing';
import { useCarrier } from '../../contexts/CarrierContext';
import useUnsavedGuard from '../../hooks/useUnsavedGuard';
import CarrierSwitcher from '../carrier/CarrierSwitcher';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { useNavigate } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import './DesignPage.css';
import { designBuildingsInBbox } from '../../lib/buildingsApi';
import { streetsInBbox, obstaclesInBbox } from '../../lib/infrastructureApi';
import { createDesignProject, createSeed, deleteSeed, loadProject, updateSeed, snapshotDesignVersion, saveDesignManual } from '../../lib/designApi';
import { runAndWatch, resumeActive } from '../../lib/designJobFlow';
import { useSeedDrop, SeedTypePicker, SEED_FA } from './designTools';
import LocationSearch from './LocationSearch';
import { DesignPanel } from './DesignPanel';
import { DesignLayer } from './DesignLayer';
import { MapRotateControl, MapTypeToggle, MapHelpCluster, useCtrlCamera } from './designMapControls';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import DesignHelpModal from './DesignHelpModal';
import DesignSpinner from './DesignSpinner';
import BuildingCandidateReview from './BuildingCandidateReview';
import DesignDetailOverlay from './DesignDetailOverlay';
import DesignHistoryDrawer from './DesignHistoryDrawer';
import DesignEditToolbar from './DesignEditToolbar';
import useIsMobile from '../../hooks/useIsMobile';
import { polygonGeometryToPath } from '../../utils/geoJson';
import { BUILDING_COLORS, BUILDING_HALO_STYLE, designBuildingStyle, servedBuildingIds } from '../../lib/designBuildingStyle';

import { listProfiles } from '../../lib/designProfilesApi';
import { toFa } from '../../utils/faFormat';
import { PARAM_FIELDS, PROFILE_KEYS, PROFILE_DEFAULTS, FEEDER_CABLES_BY_DUCT,
  MAX_COMMON_SPLIT, RADIUS, normalizeProfileParams, snapCables } from '../../shared/designParamOptions';

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
function readOutlineOnly(key) {
  try { return !!key && window.localStorage.getItem(key) === 'true'; }
  catch (e) { return false; } // Private browsing or blocked storage must not break the map.
}

const BAHARESTAN = { lat: 32.494, lng: 51.768 }; // Isfahan PoC focus
// The building / street / obstacle context loads whenever the viewport holds a
// RENDERABLE number of buildings (sparse areas show at any working zoom), NOT
// behind a blunt zoom floor. Guards: skip province/country views (FAR_ZOOM), and
// skip a viewport too dense to draw (RENDER_CAP) - e.g. a whole dense city like
// Qom zoomed out. The *_in_bbox RPCs are LIMITed server-side so they never time
// out; over the cap we clear the context and show a "zoom in" hint.
const CONTEXT_FAR_ZOOM = 11;
const CONTEXT_RENDER_CAP = 8000;

// Re-seeding: every device comes back as itself. A cabinet seeds as its own type (an MFAT
// as 'mfat_l2', which the engine badges an MFAT again and whose port 1 serves its own
// households, round 22 answer 6; as an 'odc' it was a seeded ODC under the 32-FAT floor),
// and every FAT leaf (new 'fat' + legacy mfat/sfat) as 'fat'. applyRerun and pinCabinets
// share the cabinet list, so pinning a mixed design keeps its MFATs.
const CABINET_SEED_TYPES = ['olt', 'odc', 'mfat_l2'];
const DEVICE_TYPES = [...CABINET_SEED_TYPES, 'mfat', 'sfat', 'fat'];

function sameDevicePoint(a, b) {
  const p = a.point || (a.geometry && a.geometry.coordinates);
  const q = b.point || (b.geometry && b.geometry.coordinates);
  return a.type === b.type && p && q
    && Math.abs(p[0] - q[0]) <= 1e-7 && Math.abs(p[1] - q[1]) <= 1e-7;
}

// Capture pairs on load, before edits change a pin's point. Row-id order makes the
// pairing stable across unordered DB reads, including co-located chassis. A seed
// with an ext_id is engine output, not a fresh pin, and never consumes a twin.
function pairPinnedDevices(elements) {
  const byId = (a, b) => String(a.id).localeCompare(String(b.id));
  const pins = elements.filter((e) => e.source === 'seed' && e.ext_id == null && e.id).sort(byId);
  const generated = elements.filter((e) => e.source === 'generated' && e.id && DEVICE_TYPES.includes(e.type)).sort(byId);
  const pairs = new globalThis.Map();
  const used = new Set();
  for (const pin of pins) {
    const twin = generated.find((e) => !used.has(e.id) && sameDevicePoint(pin, e));
    if (twin) { pairs.set(pin.id, twin.id); used.add(twin.id); }
  }
  return pairs;
}

function withoutPinnedDuplicates(elements, pairs) {
  const twins = new Set(pairs.values());
  return elements.filter((e) => !twins.has(e.id));
}

// The re-run hints for runDesign: per NEW seed id, the new seed id of the device's
// previous parent and the buildings and demand points it served itself (a FAT's
// building_ids / demand_ids, an MFAT's direct_building_ids / direct_demand_ids), and for
// a FAT the street point its cable ended at (street_foot, as foot), so the engine keeps
// every assignment and every cable the operator did not edit. Every kept device gets an
// entry, an empty one too: the server then marks it as a device with history, so an OLT
// whose cabinets were all moved or deleted is never taken for one the operator added. A
// device the operator added (ext_id edit-...) has no history, and one the operator moved
// (`moved`, by ext_id ?? id, or saved by hand as moved: attrs.fresh) counts as deleted + added:
// neither gets an entry, no hint names it.
function rerunSeedHints(devices, seedIdByExt, moved) {
  // Both a pin's row id and its twin's ext_id map to the same new seed. Block
  // that seed, not just one alias, so children cannot hint a moved/fresh parent.
  const unhinted = new Set(devices.filter((e) => moved.has(e.ext_id ?? e.id)
    || (e.attrs && e.attrs.fresh) || String(e.ext_id ?? e.id).startsWith('edit-'))
    .map((e) => seedIdByExt[e.ext_id ?? e.id]));
  const hinted = (ext) => !unhinted.has(seedIdByExt[ext]) && seedIdByExt[ext];
  const hints = {};
  for (const e of devices) {
    const id = hinted(e.ext_id ?? e.id);
    if (!id) continue;
    const attrs = e.attrs || {};
    const mfat = e.type === 'mfat_l2';
    const hint = {};
    if (e.type === 'olt' && (attrs.site_origin === 'engine'
      || (!Object.prototype.hasOwnProperty.call(attrs, 'site_origin')
        && e.source === 'generated' && attrs.housing === 'site' && !attrs.site_ext))) {
      hint.site_origin = 'engine';
    }
    if (hinted(e.parent_ext)) hint.parent_ext = seedIdByExt[e.parent_ext];
    const buildings = mfat ? attrs.direct_building_ids : attrs.building_ids;
    const demand = mfat ? attrs.direct_demand_ids : attrs.demand_ids;
    if (Array.isArray(buildings) && buildings.length) hint.building_ids = buildings;
    if (Array.isArray(demand) && demand.length) hint.demand_ids = demand;
    if (e.type === 'fat' && Array.isArray(attrs.street_foot)) hint.foot = attrs.street_foot;
    hints[id] = hint;
  }
  return hints;
}

const HINT = {
  fontSize: 11.5, color: '#cbd5e1', background: 'rgba(15,23,42,0.92)',
  padding: '8px 10px', borderRadius: 10, lineHeight: 1.7,
};
// Project parameter fields. colorScheme keeps the native option list dark, so the
// light option text never lands on a white popup.
const FIELD = {
  padding: 8, borderRadius: 8, border: '1px solid rgba(148,163,184,0.32)', colorScheme: 'dark',
  background: 'rgba(51,65,85,0.7)', color: '#e2e8f0', fontFamily: "'Vazirmatn', sans-serif",
};
// A select's native popup takes the select's background, and a translucent one can land
// on a white popup base: the select is opaque and every option names its own colours.
const SELECT = { ...FIELD, background: '#334155', minWidth: 0 };
const OPTION = { background: '#1e293b', color: '#e2e8f0' };
// A disabled option names its own muted colour: the browser's GrayText can sink too low on slate.
const OPTION_OFF = { background: '#1e293b', color: '#94a3b8' };
const FIELD_LABEL = { color: '#e2e8f0', fontWeight: 700 };
const FIELD_HELP = { color: '#94a3b8', lineHeight: 1.7 };
// A note that explains a disabled choice reads at label contrast, not help contrast.
const FIELD_NOTE = { color: '#f1f5f9', fontWeight: 600, lineHeight: 1.7 };
const faNum = (n) => toFa(Number(n).toLocaleString('en-US'));

// Keep the existing control ids, including the ODC's legacy single-duct id.
const PARAM_IDS = {
  odc_serving_radius_m: 'odc-serving-radius', metro_microduct_type: 'metro-microduct-type',
  odc_microduct_type: 'microduct-type', fat_microduct_type: 'fat-microduct-type',
  mfat_enable: 'distribution-mode', odc_patch_panel_max_cores: 'odc-patch-panel',
  fat_per_port: 'odc-splitter', mfat_splitter: 'mfat-splitter', fat_capacity: 'fat-splitter',
  metro_cable_cores: 'metro-cable-cores', odc_cable_cores: 'odc-cable-cores', fat_cable_cores: 'fat-cable-cores',
};
const DUCT_HELP = 'تعداد کانال ثابت است: مترو و ODC هفت‌کاناله و FAT چهارکاناله؛ فقط اندازه انتخاب می‌شود. در هر میکروداکت حداکثر ۲ کابل شوت می‌شود، هر کابل با همهٔ کرهایش در یک لوز (زیرداکت).';
const PARAM_HELP = {
  odc_serving_radius_m: 'شعاع بزرگ‌تر = کابینت و OLT کمتر، اما کابل توزیع بلندتر. پیش‌فرض ۵۰۰ متر (مسیر زمینی) طبق پاسخ تیم طراحی.',
  metro_microduct_type: DUCT_HELP, odc_microduct_type: DUCT_HELP, fat_microduct_type: DUCT_HELP,
  mfat_enable: 'ترکیبی: کابینتِ کوچک‌تر از کوچک‌ترین ODC یا روستای دور MFAT می‌شود. تمام ODC: هیچ MFATی ساخته نمی‌شود و محلهٔ کوچک یک ODC کم‌پُر می‌گیرد و فقط پورت‌های لازمش اسپلیتر دارد.',
  odc_patch_panel_max_cores: 'پچ‌پنل استاندارد اپراتور: همهٔ ODCها با همین پچ‌پنل برآورد می‌شوند و سقف پورت هر ODC همین است. پچ‌پنل بزرگ‌تر = ODC کمتر، اما کابل FAT بلندتر.',
  metro_cable_cores: 'کابل رینگ بین OLTها، داخل میکروداکت مترو. بخشی از رینگ که بارش از یک کابل بیشتر شود کابل دوم می‌گیرد؛ بیش از ۲ کابل در یک میکروداکت مجاز نیست.',
  odc_cable_cores: 'هر ODC به اندازهٔ کرهای پچ‌پنلش فیبر می‌گیرد؛ اگر یک کابل کافی نباشد، کابل دوم در لوزِ دیگر همان میکروداکت (حداکثر ۲ کابل).',
};

// options: [value, text, disabled?]. A numeric value comes back as a number, a text one as text.
function SelectField({ id, label, value, onChange, options, help, note, disabled = false }) {
  const describedBy = [note && `${id}-note`, help && `${id}-help`].filter(Boolean).join(' ');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <label htmlFor={id} style={FIELD_LABEL}>{label}</label>
      <select id={id} value={value} disabled={disabled}
        onChange={(e) => onChange(typeof value === 'number' ? Number(e.target.value) : e.target.value)}
        aria-describedby={describedBy || undefined} style={SELECT}>
        {options.map(([v, t, off]) => (
          <option key={v} value={v} disabled={!!off} style={off ? OPTION_OFF : OPTION}>{t}</option>
        ))}
      </select>
      {note && <div id={`${id}-note`} style={FIELD_NOTE}>{note}</div>}
      {help && <div id={`${id}-help`} style={FIELD_HELP}>{help}</div>}
    </div>
  );
}
function btn(kind, flex) {
  const base = {
    padding: '8px 12px', borderRadius: 9, fontWeight: 700, fontSize: 12.5,
    cursor: 'pointer', fontFamily: "'Vazirmatn', sans-serif", flex,
  };
  if (kind === 'primary') {
    return { ...base, color: '#0b1220', background: 'linear-gradient(135deg,#2dd4bf,#0d9488)', border: 'none' };
  }
  return { ...base, color: '#e2e8f0', background: 'rgba(51,65,85,0.7)', border: '1px solid rgba(148,163,184,0.32)' };
}

const GEOMETRY_FAILURE = 'بارگذاری نتیجهٔ طرح ناموفق بود؛ دوباره تلاش کنید.';
async function readProjectGeometry(projectId) {
  try {
    const loaded = await loadProject(projectId);
    return loaded.error ? { error: GEOMETRY_FAILURE } : loaded;
  } catch { return { error: GEOMETRY_FAILURE }; }
}

function InnerDesignPage() {
  const map = useMap();
  const navigate = useNavigate();
  const { session, profile } = useAuth();
  const canReviewCandidates = profile?.status === 'approved' && ['operator', 'admin', 'super_admin'].includes(profile?.role);
  const outlineStorageKey = session?.user?.id ? `fttx:design:buildings:outline-only:${session.user.id}` : null;
  const [outlineOnly, setOutlineOnly] = useState(() => readOutlineOnly(outlineStorageKey));
  useEffect(() => { setOutlineOnly(readOutlineOnly(outlineStorageKey)); }, [outlineStorageKey]);
  const pickOutlineOnly = (on) => {
    setOutlineOnly(on);
    try { if (outlineStorageKey) window.localStorage.setItem(outlineStorageKey, String(on)); }
    catch (e) { /* Keep the control usable when storage is unavailable. */ }
  };
  const [candidateDirty, setCandidateDirty] = useState(false);
  const reloadViewportRef = useRef(null);
  const onCandidateAccepted = useCallback(async () => {
    setCandidateDirty(true);
    await reloadViewportRef.current?.();
  }, []);
  const annoRef = useRef([]);
  const buildingsRef = useRef(new globalThis.Map()); // building id -> polygon and decorative halo
  const hoverableRef = useRef(false);   // current "buildings are hoverable" state (read by reload)
  const streetLinesRef = useRef([]);    // street polylines, toggled clickable for the name tooltip
  const streetTipRef = useRef(null);    // shared InfoWindow: street name on hover
  const streetHoverableRef = useRef(true); // streets show their name unless drawing/seeding/editing
  const seedMarkersRef = useRef([]);
  const rectRef = useRef(null);
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState(null);   // operator's project name -> KMZ/DDR labels
  const [seedActive, setSeedActive] = useState(false);
  const [mode, setMode] = useState('greenfield');
  const [designParams, setDesignParams] = useState(() => ({ ...PROFILE_DEFAULTS }));
  const setParam = (key, value) => setDesignParams((p) => snapCables({ ...p, [key]: value }));
  const { fat_per_port: odcSplitter, mfat_splitter: mfatSplitter,
    fat_capacity: fatSplitter, mfat_enable: mfatEnable } = designParams;
  const maximumSplit = Math.max(odcSplitter, mfatEnable ? mfatSplitter : 0) * fatSplitter;
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState('');
  const [profileName, setProfileName] = useState('');
  const [profileVersionNo, setProfileVersionNo] = useState(null);
  const [profileError, setProfileError] = useState('');
  const projectOpenedRef = useRef(false);
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [profilesError, setProfilesError] = useState('');
  const profilesAliveRef = useRef(true);
  const profilesRequestRef = useRef(0);
  const canCreate = profilesLoaded && !profilesError && !!profiles.find((preset) => preset.id === profileId)?.version?.id;
  const prefillProfile = useCallback((selected) => {
    try {
      setDesignParams(normalizeProfileParams(selected?.version?.params));
      setProfileName(selected?.name || '');
      setProfileVersionNo(selected?.version?.version_no || null);
      setProfileError('');
      return true;
    } catch (error) {
      setProfileError(`پیش‌تنظیم «${toFa(selected?.name || '')}» اعمال نشد. ${error instanceof RangeError ? error.message : 'مقدارهای ذخیره‌شده نامعتبر است.'}`);
      return false;
    }
  }, []);
  const loadProfiles = useCallback(async () => {
    const request = ++profilesRequestRef.current;
    const current = () => profilesAliveRef.current && request === profilesRequestRef.current;
    setProfilesLoaded(false);
    setProfilesError('');
    try {
      const response = await listProfiles();
      if (!current()) return;
      if (response?.error) {
        setProfiles([]); setProfileId(''); setProfilesError(response.error);
        return;
      }
      const rows = (response?.profiles || []).filter((row) => row.status === 'published' && row.version?.id);
      setProfiles(rows);
      setProfilesLoaded(true);
      const selected = rows.find((p) => p.is_default) || rows[0];
      // A slow list response must not rename a project already opened from history.
      if (!selected) setProfileId('');
      else if (!projectOpenedRef.current && prefillProfile(selected)) setProfileId(selected.id);
    } catch (error) {
      if (current() && error?.name !== 'AbortError') {
        setProfiles([]); setProfileId('');
        setProfilesError('دریافت پیش‌تنظیم‌ها ناموفق بود؛ دوباره تلاش کنید.');
      }
    }
  }, [prefillProfile]);
  useEffect(() => {
    profilesAliveRef.current = true;
    loadProfiles();
    return () => { profilesAliveRef.current = false; profilesRequestRef.current += 1; };
  }, [loadProfiles]);
  const pickProfile = (id) => {
    if (prefillProfile(profiles.find((p) => p.id === id))) setProfileId(id);
  };
  const [result, setResult] = useState(null);
  const [outputPruned, setOutputPruned] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [region, setRegion] = useState(null);
  const [kbOpen, setKbOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [mapType, setMapType] = useState('roadmap');
  // `running` drives the FULL-SCREEN blocking DesignSpinner and is reserved for
  // the map-initiated flows (seed drag-relocate, edit re-run, resume-on-mount)
  // that have no other progress UI. A PANEL-initiated async run instead sets
  // `panelBusy` (non-blocking): the panel renders its own live progress strip +
  // cancel button, and a citywide job can run for minutes - a blocking overlay
  // would make that strip and its لغو button unreachable for the whole run.
  // While panelBusy, the stale-geometry actions (edit mode, save version) are
  // disabled instead of blocking the whole screen.
  const [running, setRunning] = useState(false);
  const [panelBusy, setPanelBusy] = useState(false);
  const [seedType, setSeedType] = useState('olt');
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [detailed, setDetailed] = useState(false);
  const [hoverInfo, setHoverInfo] = useState(null);     // transient preview (follows the cursor)
  const [lockedInfo, setLockedInfo] = useState(null);   // click-to-lock selection (null = free hover)
  const [historyOpen, setHistoryOpen] = useState(false);
  const [snapshotView, setSnapshotView] = useState(null); // {id,name,version} = viewing a frozen version (read-only)
  const [versionBusy, setVersionBusy] = useState(false);
  const [versionMsg, setVersionMsg] = useState(null);     // {ok, t} transient save-version confirmation
  const [editMode, setEditMode] = useState(false);        // topology edit mode (move/add/delete in-memory)
  const { carrier } = useCarrier();
  // Conservatively protect the entire manual edit session, including adds and deletes.
  useUnsavedGuard(editMode, `ویرایش دستی طرح «${projectName || 'بدون نام'}» (${carrier?.brand_name || 'اپراتور فعال'})`);
  const [editType, setEditType] = useState(null);         // armed add type (olt/odc/fat/hh) or null
  const [editBusy, setEditBusy] = useState(false);
  const [editMsg, setEditMsg] = useState(null);
  const [zoomGated, setZoomGated] = useState(false);      // viewport too wide -> context layer suppressed
  const editCounterRef = useRef(0);                       // unique suffix for added elements
  const movedRef = useRef(new Set());                     // ext_id ?? id moved in this edit session
  const pinnedTwinsRef = useRef(new globalThis.Map());     // pin row id -> generated twin row id, captured on load
  const mapWatchRef = useRef(null);                       // live job watcher for the map-page run paths ({stop})
  const workingPrunedRef = useRef(false);                 // preserve the working marker while a version is open
  const workingSummaryRef = useRef(undefined);            // the working design's summary while a version is open
  const lockedRef = useRef(false);
  lockedRef.current = !!lockedInfo;                      // read synchronously by the map-listener closures
  const editModeRef = useRef(editMode);
  editModeRef.current = editMode;                        // read by the job-update callbacks (keeps them stable, no editMode dep)
  const isMobile = useIsMobile(960);
  const servedIds = useMemo(() => servedBuildingIds(result), [result]);
  // In edit mode each device has one handle: the pin, never its history row.
  const layerResult = useMemo(() => (editMode && result
    ? { ...result, elements: withoutPinnedDuplicates(result.elements || [], pinnedTwinsRef.current) } : result), [editMode, result]);
  const selectedBuildingId = lockedInfo?.kind === 'building' ? String(lockedInfo.data.id) : null;
  // Read current presentation from the idle listener without re-fetching the bbox.
  const buildingViewRef = useRef(null);
  const styledBuildingViewRef = useRef(null);
  buildingViewRef.current = { servedIds, selectedBuildingId, outlineOnly };

  // Ctrl-drag rotates the heading, Ctrl-wheel pitches the tilt (vector camera).
  useCtrlCamera(map);

  // Keep the live map's base layer in sync with the satellite / roadmap toggle.
  useEffect(() => {
    if (!map) return;
    map.setMapTypeId(mapType);
  }, [map, mapType]);

  // annotation context: buildings / streets / obstacles for the current view
  useEffect(() => {
    if (!map) return undefined;
    const clear = () => {
      annoRef.current.forEach((o) => o.setMap(null));
      annoRef.current = [];
      buildingsRef.current.clear();
      streetLinesRef.current = [];
      if (streetTipRef.current) streetTipRef.current.close();
    };
    let disposed = false;
    let loadVersion = 0;
    const drawBuildings = (rows) => rows.forEach((row) => {
      try {
        const paths = polygonGeometryToPath(row.geometry);
        const id = String(row.id);
        const old = buildingsRef.current.get(id);
        if (old) {
          old.poly.setMap(null); old.halo.setMap(null);
          annoRef.current = annoRef.current.filter((overlay) => overlay !== old.poly && overlay !== old.halo);
        }
        const view = buildingViewRef.current;
        const halo = new window.google.maps.Polygon({ paths, ...BUILDING_HALO_STYLE, map });
        const poly = new window.google.maps.Polygon({ paths, clickable: hoverableRef.current, map,
          ...designBuildingStyle({ served: view.servedIds.has(id), selected: view.selectedBuildingId === id, outlineOnly: view.outlineOnly }),
        });
        const info = { kind: 'building', data: {
          id: row.id, floor_count: row.floor_count, unit_count: row.unit_count,
          unit_source: row.unit_source, has_entry: row.has_entry, facade: row.facade, land_use: row.land_use,
        } };
        poly.addListener('mouseover', () => { if (!lockedRef.current) setHoverInfo(info); });
        poly.addListener('click', () => setLockedInfo(info));
        annoRef.current.push(halo, poly);
        buildingsRef.current.set(id, { id, poly, halo });
      } catch (e) { /* skip bad geometry */ }
    });
    const reload = async (throwOnError = false) => {
      const version = ++loadVersion;
      const b = map.getBounds();
      if (!b) return;
      // Skip only province/country-level views (polygons are invisible specks).
      if ((map.getZoom() || 0) < CONTEXT_FAR_ZOOM) {
        clear();
        setZoomGated(true);
        return;
      }
      const j = b.toJSON();
      const [bs, ss, os] = await Promise.all([designBuildingsInBbox(j), streetsInBbox(j), obstaclesInBbox(j)]);
      if (disposed || version !== loadVersion) return;
      if (throwOnError && (bs.error || ss.error || os.error)) throw new Error('Viewport reload failed');
      // Density gate: render whenever the building count is renderable - sparse
      // areas always pass; only a dense city zoomed out exceeds the cap.
      if ((bs.rows || []).length > CONTEXT_RENDER_CAP) {
        clear();
        setZoomGated(true);
        return;
      }
      setZoomGated(false);
      clear();
      drawBuildings(bs.rows || []);
      (ss.rows || []).forEach((row) => {
        const path = ((row.geometry && row.geometry.coordinates) || []).map(([lng, lat]) => ({ lat, lng }));
        if (path.length < 2) return;
        // Visible context line (non-interactive).
        annoRef.current.push(new window.google.maps.Polyline({
          path, clickable: false, strokeColor: '#64748b', strokeWeight: 2, zIndex: 2, map,
        }));
        if (!row.name) return;
        // A 2px line is nearly impossible to land the cursor on, so a transparent
        // WIDE hit-line on top carries the name tooltip (cursor-following). It is
        // clickable only when not drawing/seeding/editing, so it never eats the
        // clicks those modes need.
        const hit = new window.google.maps.Polyline({
          path, clickable: streetHoverableRef.current, strokeColor: '#64748b',
          strokeOpacity: 0, strokeWeight: 14, zIndex: 3, map,
        });
        hit.addListener('mouseover', (e) => {
          if (!streetTipRef.current) {
            streetTipRef.current = new window.google.maps.InfoWindow({ disableAutoPan: true });
          }
          const el = document.createElement('div');
          el.textContent = row.name;
          el.style.cssText = 'direction:rtl;font-family:Vazirmatn,Vazir,Tahoma,sans-serif;'
            + 'font-size:12px;font-weight:600;color:#0f172a;white-space:nowrap;padding:1px 3px;';
          streetTipRef.current.setContent(el);
          if (e && e.latLng) streetTipRef.current.setPosition(e.latLng);
          streetTipRef.current.open({ map });
        });
        hit.addListener('mousemove', (e) => {
          if (streetTipRef.current && e && e.latLng) streetTipRef.current.setPosition(e.latLng);
        });
        hit.addListener('mouseout', () => { if (streetTipRef.current) streetTipRef.current.close(); });
        annoRef.current.push(hit);
        streetLinesRef.current.push(hit);
      });
      (os.rows || []).forEach((row) => {
        const ring = row.geometry && row.geometry.coordinates && row.geometry.coordinates[0];
        if (!ring) return;
        annoRef.current.push(new window.google.maps.Polygon({
          paths: ring.map(([lng, lat]) => ({ lat, lng })), clickable: false,
          fillColor: '#7f1d1d', fillOpacity: 0.18, strokeColor: '#b91c1c', strokeWeight: 1, zIndex: 1, map,
        }));
      });
    };
    reloadViewportRef.current = () => reload(true);
    const l = map.addListener('idle', () => reload());
    reload();
    return () => { disposed = true; reloadViewportRef.current = null; window.google.maps.event.removeListener(l); clear(); };
  }, [map]);

  // Assignment/presentation changes affect the viewport; a selection change only
  // touches the old and new building pairs, even at the 8000-building render cap.
  useEffect(() => {
    const previous = styledBuildingViewRef.current;
    const restyle = ({ id, poly, halo }) => {
      poly.setOptions(designBuildingStyle({
        served: servedIds.has(id), selected: selectedBuildingId === id, outlineOnly,
      }));
      halo.setOptions(BUILDING_HALO_STYLE);
    };
    if (!previous || previous.servedIds !== servedIds || previous.outlineOnly !== outlineOnly) {
      buildingsRef.current.forEach(restyle);
    } else if (previous.selectedBuildingId !== selectedBuildingId) {
      new Set([previous.selectedBuildingId, selectedBuildingId]).forEach((id) => {
        const building = buildingsRef.current.get(id);
        if (building) restyle(building);
      });
    }
    styledBuildingViewRef.current = { servedIds, selectedBuildingId, outlineOnly };
  }, [servedIds, selectedBuildingId, outlineOnly]);

  // Building footprints become hoverable only in detailed mode AND when not
  // drawing/placing (so they never eat the map clicks those modes need). reload()
  // reads hoverableRef for freshly-built polygons; this updates the existing ones.
  useEffect(() => {
    hoverableRef.current = detailed && !drawMode && !seedActive && !editMode;
    buildingsRef.current.forEach(({ poly }) => poly.setOptions({ clickable: hoverableRef.current }));
    // Street name tooltips are useful during free browse (not gated on detailed),
    // but must yield their clicks while drawing/seeding/editing.
    streetHoverableRef.current = !drawMode && !seedActive && !editMode;
    streetLinesRef.current.forEach((p) => p.setOptions({ clickable: streetHoverableRef.current }));
    if (!streetHoverableRef.current && streetTipRef.current) streetTipRef.current.close();
  }, [detailed, drawMode, seedActive, editMode]);

  // Shared normalized handler for the map-page async job paths (seed
  // drag-relocate, edit-mode re-run, resume-on-mount). On 'complete' the worker
  // already persisted the geometry (designJobFlow.attachWatch loads it), so we
  // just render it; every terminal status clears the spinner. Declared BEFORE
  // the seed-marker effect that lists it as a dependency (no-use-before-define).
  const capturePinnedTwins = useCallback((elements) => {
    pinnedTwinsRef.current = pairPinnedDevices(elements || []);
  }, []);

  const onMapJobUpdate = useCallback((u) => {
    if (!u) return;
    if (u.status === 'already_running') {
      // Another job was already in flight, so THIS request (e.g. the seed move)
      // was NOT enqueued. Tell the operator, but keep watching the active job so
      // the spinner clears when it reaches a terminal state.
      window.alert('یک اجرای دیگر در حال انجام است؛ تغییرات شما در این اجرا اعمال نشده است');
      setRunning(true);
      return;
    }
    if (u.status === 'complete') {
      if (u.result) { setCandidateDirty(false); workingPrunedRef.current = false; capturePinnedTwins(u.result.elements); setResult(u.result); setOutputPruned(false); }
      setRunning(false);
      if (editModeRef.current) { setEditMode(false); setEditType(null); }
      return;
    }
    if (u.status === 'failed') {
      setRunning(false);
      if (editModeRef.current) setEditMsg({ ok: false, t: u.error === GEOMETRY_FAILURE ? GEOMETRY_FAILURE : 'اجرای مجدد ناموفق بود' });
      else if (u.error === GEOMETRY_FAILURE) setVersionMsg({ ok: false, t: GEOMETRY_FAILURE });
      return;
    }
    if (u.status === 'cancelled') { setRunning(false); return; }
    // queued/running -> keep the spinner up
    setRunning(true);
  }, [capturePinnedTwins]);

  useEffect(() => {
    if (!map) return undefined;
    seedMarkersRef.current.forEach((m) => m.setMap(null));
    seedMarkersRef.current = [];
    if (editMode) return undefined; // edit mode renders ALL element markers (incl. seeds) via DesignLayer
    const seeds = ((result && result.elements) || []).filter((e) => e.source === 'seed');
    seeds.forEach((e) => {
      const c = e.point || (e.geometry && e.geometry.coordinates);
      if (!c) return;
      const marker = new window.google.maps.Marker({
        position: { lat: c[1], lng: c[0] }, map, draggable: true,
        title: `${SEED_FA[e.type] || e.type} - بکش تا جابه‌جا شود`,
      });
      marker.addListener('dragend', async (ev) => {
        const pt = { type: 'Point', coordinates: [ev.latLng.lng(), ev.latLng.lat()] };
        setRunning(true);
        // ASYNC run: kick off the job and watch it (the watcher clears `running`
        // + sets the new result on complete). runDesign now returns 202 {job_id},
        // never {result}, so we must NOT await a synchronous result here. A
        // THROWN updateSeed/runAndWatch (network down) must clear the blocking
        // spinner instead of leaving it up forever.
        try {
          await updateSeed(e.id, pt);
          // the moved row still holds its old parent and households: run it as fresh
          const handle = await runAndWatch(projectId, onMapJobUpdate, undefined, [e.id]);
          if (handle) {
            if (mapWatchRef.current) mapWatchRef.current.stop();
            mapWatchRef.current = handle;
          } else {
            setRunning(false); // run failed to enqueue (onMapJobUpdate already got 'failed')
          }
        } catch {
          setRunning(false);
          window.alert('اجرای طراحی پس از جابه‌جایی ناموفق بود؛ اتصال اینترنت را بررسی کنید و دوباره تلاش کنید');
        }
      });
      seedMarkersRef.current.push(marker);
    });
    return () => { seedMarkersRef.current.forEach((m) => m.setMap(null)); seedMarkersRef.current = []; };
  }, [map, result, projectId, editMode, onMapJobUpdate]);

  // Draw-region: 2 clicks set an initial rectangle, which then becomes an
  // editable + draggable polygon - drag any of its 4 corners to reshape/rotate
  // it, or drag the whole shape to move it, until a project is created.
  useEffect(() => {
    if (!map || !drawMode) return undefined;
    const corners = [];
    const listener = map.addListener('click', (ev) => {
      corners.push({ lat: ev.latLng.lat(), lng: ev.latLng.lng() });
      if (corners.length < 2) return;
      const lats = corners.map((c) => c.lat);
      const lngs = corners.map((c) => c.lng);
      const n = Math.max(...lats), s = Math.min(...lats), e = Math.max(...lngs), w = Math.min(...lngs);
      const path = [{ lat: n, lng: w }, { lat: n, lng: e }, { lat: s, lng: e }, { lat: s, lng: w }];
      if (rectRef.current) rectRef.current.setMap(null);
      const poly = new window.google.maps.Polygon({
        paths: path, editable: true, draggable: true, zIndex: 3, map,
        fillColor: '#2dd4bf', fillOpacity: 0.08, strokeColor: '#2dd4bf', strokeWeight: 2,
      });
      rectRef.current = poly;
      const sync = () => setRegion(poly.getPath().getArray().map((p) => [p.lng(), p.lat()]));
      sync();
      const gp = poly.getPath();
      gp.addListener('set_at', sync);
      gp.addListener('insert_at', sync);
      gp.addListener('remove_at', sync);
      poly.addListener('dragend', sync);
      setDrawMode(false);
    });
    return () => { window.google.maps.event.removeListener(listener); };
  }, [map, drawMode]);

  // Edit mode: with an add-type armed, a map click appends a new element to the
  // in-memory result (persisted later by a save action). drawMode/seedActive are
  // forced off in edit mode, so this is the only active map-click listener.
  useEffect(() => {
    if (!map || !editMode || !editType) return undefined;
    const prevCursor = map.get('draggableCursor');
    map.setOptions({ draggableCursor: 'crosshair' });
    const l = map.addListener('click', (ev) => {
      if (!ev.latLng) return;
      const pt = [ev.latLng.lng(), ev.latLng.lat()];
      editCounterRef.current += 1;
      const id = `edit-${editType}-${editCounterRef.current}`;
      const type = editType;       // team spec 2026-07-06: a manual FAT is the single 'fat' leaf
      const source = editType === 'hh' ? 'generated' : 'seed';   // olt/odc/fat re-seed on re-run
      const el = { ext_id: id, type, source, point: pt, parent_ext: null, ring: 'A', attrs: {} };
      setResult((prev) => ({ ...(prev || {}), elements: [...((prev && prev.elements) || []), el] }));
    });
    return () => {
      window.google.maps.event.removeListener(l);
      map.setOptions({ draggableCursor: prevCursor || null });
    };
  }, [map, editMode, editType]);

  const reloadDesign = useCallback(async () => {
    if (!projectId) return;
    setOutputPruned(false);
    const { elements, routes, error } = await readProjectGeometry(projectId);
    if (error) { setResult(null); setVersionMsg({ ok: false, t: error }); return; }
    setVersionMsg(null);
    capturePinnedTwins(elements);
    // loadProject returns geometry only: keep the shown summary, or the legend loses the
    // design's capacity_model and a manual save would overwrite the stored summary.
    setResult((prev) => ({ elements, routes, summary: prev ? prev.summary : undefined }));
    setOutputPruned(workingPrunedRef.current);
  }, [projectId, capturePinnedTwins]);

  // Fit the map to a loaded design's elements + routes.
  const fitMapToDesign = useCallback((elements, routes) => {
    if (!map) return;
    const b = new window.google.maps.LatLngBounds();
    (elements || []).forEach((e) => {
      const c = e.point || (e.geometry && e.geometry.coordinates);
      if (c) b.extend({ lat: c[1], lng: c[0] });
    });
    (routes || []).forEach((r) => {
      ((r.line || (r.geometry && r.geometry.coordinates)) || []).forEach(([lng, lat]) => b.extend({ lat, lng }));
    });
    if (!b.isEmpty()) map.fitBounds(b, 64);
  }, [map]);

  // Resume-on-mount: when a project is opened (or switched to), re-attach to any
  // ACTIVE (queued|running) job so a page reload mid-run does not lose the run.
  // Keyed on projectId; the stored watcher is stopped when the project changes
  // or the page unmounts.
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    (async () => {
      const handle = await resumeActive(projectId, onMapJobUpdate);
      if (handle) {
        if (cancelled) { handle.stop(); return; }
        if (mapWatchRef.current) mapWatchRef.current.stop();
        mapWatchRef.current = handle;
        setRunning(true);
      }
    })();
    return () => {
      cancelled = true;
      if (mapWatchRef.current) { mapWatchRef.current.stop(); mapWatchRef.current = null; }
    };
  }, [projectId, onMapJobUpdate]);

  // Open a project from history: load its elements/routes, switch to its mode,
  // and fit the map to the design (we do NOT redraw the editable region rect -
  // that is a drawing tool; the rendered design defines the view instead).
  const openHistoryProject = useCallback(async (p) => {
    setHistoryOpen(false);
    setCandidateDirty(false);
    setSnapshotView(null);
    setOutputPruned(false);
    workingPrunedRef.current = !!p.output_pruned_at;
    setEditMode(false); setEditType(null);
    if (rectRef.current) { rectRef.current.setMap(null); rectRef.current = null; }
    setMode(p.mode || 'greenfield');
    setSeedActive(false);
    setDrawMode(false);
    setDetailed(false);
    setHoverInfo(null);
    setLockedInfo(null);
    projectOpenedRef.current = true;
    setProjectId(p.id);
    setProjectName(p.name || null);
    setProfileName(p.profile_name || p.design_profiles?.name || '');
    setProfileVersionNo(p.profile_version_no || null);
    setResult(null);
    const { elements, routes, error } = await readProjectGeometry(p.id);
    if (error) { setVersionMsg({ ok: false, t: error }); return; }
    setVersionMsg(null);
    capturePinnedTwins(elements);
    setResult({ elements, routes, summary: p.result_summary || undefined });
    setOutputPruned(workingPrunedRef.current);
    fitMapToDesign(elements, routes);
  }, [fitMapToDesign, capturePinnedTwins]);

  // Save a frozen version snapshot of the current working design.
  const saveVersion = useCallback(async () => {
    if (!projectId) return;
    setVersionBusy(true);
    const { version, error } = await snapshotDesignVersion(projectId);
    setVersionBusy(false);
    setVersionMsg(error
      ? { ok: false, t: error }
      : { ok: true, t: `نسخهٔ ${Number(version).toLocaleString('fa-IR')} ذخیره شد` });
    setTimeout(() => setVersionMsg(null), 3500);
  }, [projectId]);

  // Surface a failed post-rerun snapshot via the version-message channel: the
  // rerun itself succeeded and edit mode exits on completion, so the edit
  // toolbar's msg slot would no longer be visible.
  const showSnapshotFailure = useCallback(() => {
    setVersionMsg({ ok: false, t: 'اجرای مجدد انجام شد ولی ذخیرهٔ نسخه ناموفق بود' });
    setTimeout(() => setVersionMsg(null), 5000);
  }, []);

  // View a frozen snapshot read-only: render it without touching projectId/mode,
  // so a run can never overwrite it. The working project stays the editable one.
  const openVersionView = useCallback(async (v) => {
    if (!snapshotView) workingSummaryRef.current = result ? result.summary : undefined;
    setHistoryOpen(false);
    setOutputPruned(false);
    setSeedActive(false);
    setDrawMode(false);
    setEditMode(false);
    setEditType(null);
    setHoverInfo(null);
    setLockedInfo(null);
    setSnapshotView({ id: v.id, name: v.name, version: v.version });
    setResult(null);
    const { elements, routes, error } = await readProjectGeometry(v.id);
    if (error) { setVersionMsg({ ok: false, t: error }); return; }
    setVersionMsg(null);
    capturePinnedTwins(elements);
    setResult({ elements, routes, summary: v.result_summary || undefined });
    setOutputPruned(!!v.output_pruned_at);
    fitMapToDesign(elements, routes);
  }, [fitMapToDesign, snapshotView, result, capturePinnedTwins]);

  // Leave the read-only snapshot view and return to the working project (or blank).
  const exitVersionView = useCallback(async () => {
    setSnapshotView(null);
    setOutputPruned(false);
    if (projectId) {
      setResult(null);
      const { elements, routes, error } = await readProjectGeometry(projectId);
      if (error) { setVersionMsg({ ok: false, t: error }); return; }
      setVersionMsg(null);
      capturePinnedTwins(elements);
      setResult({ elements, routes, summary: workingSummaryRef.current });
      setOutputPruned(workingPrunedRef.current);
      fitMapToDesign(elements, routes);
    } else {
      setResult(null);
    }
  }, [projectId, fitMapToDesign, capturePinnedTwins]);

  // --- edit mode (move / add / delete on the in-memory result) -----------
  // Every edit session starts with no moves; a cancel discards them with the edits. An
  // applied re-run leaves edit mode, so the next session starts clean again.
  const enterEdit = useCallback(() => {
    movedRef.current = new Set();
    setEditMode(true); setEditType(null); setEditMsg(null);
    setSeedActive(false); setDrawMode(false); setDetailed(false);
    setHoverInfo(null); setLockedInfo(null);
  }, []);

  const cancelEdit = useCallback(async () => {
    movedRef.current = new Set();
    setEditMode(false); setEditType(null); setEditMsg(null);
    setLockedInfo(null); setHoverInfo(null);
    await reloadDesign(); // discard the in-memory edits
  }, [reloadDesign]);

  const onElementMove = useCallback((elementId, lngLat) => {
    movedRef.current.add(elementId);
    setResult((prev) => (prev ? {
      ...prev,
      elements: (prev.elements || []).map((e) => ((e.ext_id ?? e.id) === elementId
        ? { ...e, point: lngLat, geometry: { type: 'Point', coordinates: lngLat } } : e)),
    } : prev));
  }, []);

  const onDeleteElement = useCallback((elementId) => {
    const twinId = pinnedTwinsRef.current.get(elementId);
    setResult((prev) => (prev ? { ...prev, elements: (prev.elements || [])
      .filter((e) => (e.ext_id ?? e.id) !== elementId && (!twinId || e.id !== twinId)) } : prev));
    setLockedInfo(null); setHoverInfo(null);
  }, []);

  const onElementSelectEdit = useCallback((info) => setLockedInfo(info), []);

  // Manual save: persist the edited topology as-is (flagged needs-recompute) + snapshot.
  // A moved or added device is saved with attrs.fresh: the rows keep ext_id, parent_ext
  // and attrs, which the next run would read as its history, and the move would be lost.
  // The shown result keeps the mark too, for a later re-run or save in this page.
  const saveManualEdit = useCallback(async () => {
    if (!projectId || !result) return;
    setEditBusy(true); setEditMsg(null);
    const edited = (e) => movedRef.current.has(e.ext_id ?? e.id) || String(e.ext_id ?? e.id).startsWith('edit-');
    const byId = new globalThis.Map((result.elements || []).map((e) => [e.id, e]));
    const elements = withoutPinnedDuplicates(result.elements || [], pinnedTwinsRef.current).map((e) => {
      const twin = pinnedTwinsRef.current.has(e.id) ? byId.get(pinnedTwinsRef.current.get(e.id)) : null;
      // Persist one row per pin with the twin's ext_id, so children's links and
      // its history survive new DB row ids and a later page reload.
      const saved = twin ? { ...twin, ...e, ext_id: twin.ext_id, parent_ext: twin.parent_ext,
        attrs: { ...(twin.attrs || {}), ...(e.attrs || {}) } } : e;
      return edited(e) ? { ...saved, attrs: { ...(saved.attrs || {}), fresh: true } } : saved;
    });
    const { error } = await saveDesignManual(projectId, { ...result, elements });
    if (!error) await snapshotDesignVersion(projectId);
    setEditBusy(false);
    if (error) { setEditMsg({ ok: false, t: error }); return; }
    capturePinnedTwins(elements);
    setResult((prev) => ({ ...prev, elements, summary: { ...((prev && prev.summary) || {}), manual_edited: true } }));
    setEditMode(false); setEditType(null);
  }, [projectId, result, capturePinnedTwins]);

  // Re-run: promote the edited OLT/ODC/MFAT/FAT to seeds, re-run the engine (re-derives
  // cabling + HH + feasibility around the operator's placements), then snapshot.
  // ASYNC: runDesign now returns 202 {job_id}; runAndWatch watches the job and a
  // local callback finalizes on complete (snapshot + render + leave edit mode).
  const applyRerun = useCallback(async () => {
    if (!projectId || !result) return;
    setEditBusy(true); setEditMsg(null);
    try {
      const { elements: dbEls, error } = await readProjectGeometry(projectId);
      if (error) throw new Error(error);
      for (const e of (dbEls || []).filter((x) => x.source === 'seed')) await deleteSeed(e.id);
      const allDevices = (result.elements || []).filter((e) => DEVICE_TYPES.includes(e.type));
      const pairs = pinnedTwinsRef.current;
      const devices = withoutPinnedDuplicates(allDevices, pairs);
      const byId = new globalThis.Map(allDevices.map((e) => [e.id, e]));
      // Only the paired row supplies history and an ext_id alias. Pairing was
      // captured before edits, so a moved pin cannot re-seed its old twin.
      const history = devices.flatMap((e) => {
        const prior = pairs.has(e.id) ? byId.get(pairs.get(e.id)) : null;
        return prior ? [{ ...prior, id: e.id, ext_id: e.ext_id ?? e.id,
          attrs: { ...(prior.attrs || {}), ...(e.attrs || {}) } }] : (e.ext_id != null ? [e] : []);
      });
      const seedIdByExt = {};
      for (const e of devices) {
        const seedType = CABINET_SEED_TYPES.includes(e.type) ? e.type : 'fat';
        const c = e.point || (e.geometry && e.geometry.coordinates);
        if (!c) continue;
        const { id } = await createSeed(projectId, seedType, { type: 'Point', coordinates: c });
        if (id && (e.ext_id ?? e.id)) seedIdByExt[e.ext_id ?? e.id] = id;
        const twin = pairs.has(e.id) ? byId.get(pairs.get(e.id)) : null;
        if (id && twin && twin.ext_id) seedIdByExt[twin.ext_id] = id;
      }
      const seedHints = rerunSeedHints(history, seedIdByExt, movedRef.current);
      // True when runDesign attached to a job that was ALREADY in flight: this
      // edit was never enqueued, so the foreign job's completion is NOT this
      // rerun's success (no snapshot, no result claim, edit mode stays open so
      // the operator can re-apply once the active run finishes).
      let foreignRun = false;
      // Re-run finalizer: snapshot + render on complete; surface a Persian error
      // on failure. Delegates running/queued ticks to keep the spinner up.
      const onRerunUpdate = async (u) => {
        if (!u) return;
        if (u.status === 'already_running') {
          foreignRun = true;
          setEditMsg({ ok: false, t: 'یک اجرای دیگر در حال انجام است؛ تغییرات شما در این اجرا اعمال نشده است' });
          return;
        }
        if (u.status === 'complete') {
          if (foreignRun) { setEditBusy(false); setRunning(false); return; }
          // A rejected/failed snapshot must NOT leave editBusy/running stuck on
          // a completed rerun: finalize in finally, report via versionMsg.
          try {
            const { error: snapErr } = await snapshotDesignVersion(projectId);
            if (snapErr) showSnapshotFailure();
          } catch {
            showSnapshotFailure();
          } finally {
            if (u.result) { setCandidateDirty(false); workingPrunedRef.current = false; capturePinnedTwins(u.result.elements); setResult(u.result); setOutputPruned(false); }
            setEditBusy(false); setRunning(false);
            setEditMode(false); setEditType(null);
          }
          return;
        }
        if (u.status === 'failed') { setEditMsg({ ok: false, t: u.error === GEOMETRY_FAILURE ? GEOMETRY_FAILURE : 'اجرای مجدد ناموفق بود' }); setEditBusy(false); setRunning(false); return; }
        if (u.status === 'cancelled') { setEditBusy(false); setRunning(false); return; }
        setRunning(true);
      };
      const handle = await runAndWatch(projectId, onRerunUpdate, seedHints);
      if (handle) {
        if (mapWatchRef.current) mapWatchRef.current.stop();
        mapWatchRef.current = handle;
      } else {
        setEditMsg({ ok: false, t: 'اجرای مجدد ناموفق بود' }); setEditBusy(false);
      }
      return;
    } catch (e) {
      setEditMsg({ ok: false, t: e?.message === GEOMETRY_FAILURE ? GEOMETRY_FAILURE : 'اجرای مجدد ناموفق بود' });
    }
    setEditBusy(false);
  }, [projectId, result, showSnapshotFailure, capturePinnedTwins]);

  // Headless seed placement; the site/ODC/FAT picker UI lives in the panel below.
  useSeedDrop({ map, active: seedActive, projectId, type: seedType, onSaved: reloadDesign });

  // «ثبت محدوده»: commit the drawn (or current-viewport) region as a new design
  // project and switch into seed-placement mode.
  const registerRegion = useCallback(async () => {
    const selected = profiles.find((p) => p.id === profileId);
    if (!map || !selected?.version?.id) return;
    let polygon;
    if (Array.isArray(region) && region.length >= 3) {
      // the drawn (and possibly reshaped) region polygon, closed into a ring
      polygon = { type: 'Polygon', coordinates: [[...region, region[0]]] };
    } else {
      // no region drawn: fall back to the current viewport bbox
      const b = map.getBounds() && map.getBounds().toJSON();
      if (!b) return;
      polygon = {
        type: 'Polygon',
        coordinates: [[
          [b.west, b.south], [b.east, b.south], [b.east, b.north], [b.west, b.north], [b.west, b.south],
        ]],
      };
    }
    const radiusValue = Number(designParams.odc_serving_radius_m);
    const radiusOnStep = Number.isFinite(radiusValue) && (radiusValue - 150) % 50 === 0;
    if (!radiusOnStep || radiusValue < 150 || radiusValue > 1000) {
      window.alert('شعاع سرویس‌دهی ODC باید بین ۱۵۰ تا ۱۰۰۰ متر و با گام ۵۰ متر باشد');
      return;
    }
    // Large-area guard: the single-pass engine can time out on a wide region, so
    // warn before submitting. zoomGated means the viewport was already too wide to
    // render the context layer; spanDeg is the bbox's larger degree span.
    const ring = polygon.coordinates[0];
    const lngs = ring.map((c) => c[0]); const lats = ring.map((c) => c[1]);
    const spanDeg = Math.max(Math.max(...lngs) - Math.min(...lngs), Math.max(...lats) - Math.min(...lats));
    const LARGE_SPAN_DEG = 0.03; // ~3.3 km; beyond this the single-pass engine may time out
    const looksLarge = zoomGated || spanDeg > LARGE_SPAN_DEG;
    if (looksLarge && !window.confirm('این محدوده بزرگ است و اجرای طراحی ممکن است طول بکشد یا به پایان نرسد. محدودهٔ کوچک‌تر توصیه می‌شود. ادامه می‌دهید؟')) {
      return;
    }
    // when a region was drawn, also stash its ring in params so the server can
    // clip the design to it (persistence reads project.params.region_polygon)
    const params = Object.fromEntries(PROFILE_KEYS.map((key) => [key, designParams[key]]));
    params.odc_serving_radius_m = radiusValue;
    params.coverage_radius_m = radiusValue;
    if (Array.isArray(region) && region.length >= 3) params.region_polygon = region;
    const newName = `طراحی ${new Date().toLocaleDateString('fa-IR')}`;
    const response = await createDesignProject(newName, polygon, mode, params, selected.version.id);
    const { id, error } = response;
    if (error || !id) {
      if (response.code === 'CARRIER_RULES_MISSING') { setProfileId(''); await loadProfiles(); }
      else setProfileError(error || 'ساخت طرح ناموفق بود.');
      return;
    }
    // lock the region outline so it stops intercepting seed-placement clicks
    if (rectRef.current) rectRef.current.setOptions({ editable: false, draggable: false, clickable: false });
    workingPrunedRef.current = false;
    projectOpenedRef.current = true;
    setProjectId(id); setProjectName(newName); setResult(null); setOutputPruned(false); setSeedActive(true);
  }, [map, mode, designParams, profileId, profiles, region, zoomGated, loadProfiles]);

  // «پروژه‌ی جدید»: wipe everything (project, result, drawn region, seed tool,
  // mode) so the operator starts a fresh design from a blank slate.
  const newProjectReset = useCallback(() => {
    setCandidateDirty(false);
    if (rectRef.current) { rectRef.current.setMap(null); rectRef.current = null; }
    workingPrunedRef.current = false;
    projectOpenedRef.current = false;
    setProjectId(null);
    setProjectName(null);
    setResult(null);
    setOutputPruned(false);
    setRegion(null);
    setSeedActive(false);
    setDrawMode(false);
    setMode('greenfield');
    setDetailed(false);
    setHoverInfo(null);
    setLockedInfo(null);
    setSnapshotView(null);
    setEditMode(false);
    setEditType(null);
    const selected = profiles.find((p) => p.id === profileId) || profiles.find((p) => p.is_default) || profiles[0];
    if (prefillProfile(selected)) setProfileId(selected?.id || '');
    else { setProfileId(''); setProfileName(''); }
  }, [profiles, profileId, prefillProfile]);

  // A cabinet that is already a seed row (every cabinet after a re-run) stays as it is:
  // pinning it again doubled it. The generated row stays beside the new seed until
  // the next run: persistedSeedHints copies its OLT site_origin at the same point,
  // including legacy generated sites. Pinning must not turn it into an operator site.
  const pinCabinets = useCallback(async () => {
    if (!projectId || !result) return;
    const cabs = withoutPinnedDuplicates(result.elements || [], pinnedTwinsRef.current)
      .filter((e) => CABINET_SEED_TYPES.includes(e.type) && e.source !== 'seed');
    for (const e of cabs) {
      const c = e.point || (e.geometry && e.geometry.coordinates);
      if (c) await createSeed(projectId, e.type, { type: 'Point', coordinates: c });
    }
    await reloadDesign();
  }, [projectId, result, reloadDesign]);

  const resetSeeds = useCallback(async () => {
    if (!projectId) return;
    const { elements, error } = await readProjectGeometry(projectId);
    if (error) { setVersionMsg({ ok: false, t: error }); return; }
    for (const e of (elements || []).filter((x) => x.source === 'seed')) {
      await deleteSeed(e.id);
    }
    await reloadDesign();
  }, [projectId, reloadDesign]);

  const onRun = useCallback((res) => {
    setCandidateDirty(false);
    workingPrunedRef.current = false;
    capturePinnedTwins(res.elements);
    setResult(res); setOutputPruned(false); setSeedActive(false);
  }, [capturePinnedTwins]);
  // hover previews (ignored while a card is locked); click locks; × unlocks.
  const onHover = useCallback((info) => { if (!lockedRef.current) setHoverInfo(info); }, []);
  const onSelect = useCallback((info) => setLockedInfo(info), []);
  const closeCard = useCallback(() => { setLockedInfo(null); setHoverInfo(null); }, []);
  const toggleDetailed = useCallback(() => { setDetailed((v) => !v); setHoverInfo(null); setLockedInfo(null); }, []);

  // Fly to a searched place (same as the annotation map): panTo + a close zoom
  // triggers the map's `idle` event, which the building-layer reload above already
  // handles, so the new viewport's buildings/streets/obstacles load with no extra wiring.
  const goTo = useCallback(({ lat, lng }) => {
    if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    map.panTo({ lat, lng });
    map.setZoom(17);
  }, [map]);

  const needsRecompute = !!(result && result.summary && result.summary.manual_edited);
  const showPrunedEmpty = !!outputPruned && !!result
    && (result.elements || []).length === 0 && (result.routes || []).length === 0;
  const prunedNotice = showPrunedEmpty ? (
    <div role="status" style={{ pointerEvents: 'none', direction: 'rtl', textAlign: 'center',
      fontFamily: "'Vazirmatn', sans-serif", padding: '14px 16px', borderRadius: 14,
      color: '#f8fafc', background: 'rgba(15,23,42,0.94)', border: '1px solid rgba(148,163,184,0.42)',
      boxShadow: '0 12px 32px rgba(2,6,23,0.48)',
      ...(isMobile
        ? { width: '100%', boxSizing: 'border-box', flex: 'none' }
        : { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 6, width: 'min(420px, calc(100vw - 32px))' }) }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, lineHeight: 1.8 }}>
        خروجی این طرح برای آزادسازی فضا حذف شده است.
      </div>
      <div style={{ marginTop: 4, color: '#cbd5e1', fontSize: 12, fontWeight: 600, lineHeight: 1.8 }}>
        پارامترها محفوظ‌اند؛ «اجرای طراحی» را بزنید تا خروجی دوباره ساخته شود.
      </div>
    </div>
  ) : null;

  return (
    <>
      <Map mapId="fttx-design" defaultCenter={BAHARESTAN} defaultZoom={16}
           gestureHandling="greedy" renderingType="VECTOR" defaultTilt={0}
           disableDefaultUI
           style={{ position: 'absolute', inset: 0 }} />
      {/* Location finder (city / district / street). Top-center on desktop;
          full-width across the top on phones, where the control panel stacks
          beneath it so the two never overlap. */}
      <div style={isMobile
        ? { position: 'absolute', top: 8, insetInlineStart: 8, insetInlineEnd: 8, zIndex: 8 }
        : { position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)',
            width: 'min(380px, 64vw)', zIndex: 8 }}>
        <LocationSearch onPick={goTo} />
      </div>
      {snapshotView && (
        <div style={{ position: 'absolute', top: isMobile ? 112 : 64, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9, direction: 'rtl', fontFamily: "'Vazirmatn', sans-serif", display: 'flex', alignItems: 'center',
          gap: 12, padding: '9px 14px', borderRadius: 12, maxWidth: 'min(92vw, 470px)', color: '#fef3c7',
          background: 'linear-gradient(135deg, rgba(180,83,9,0.96), rgba(146,64,14,0.96))',
          border: '1px solid rgba(251,191,36,0.45)', boxShadow: '0 10px 30px rgba(2,6,23,0.5)' }}>
          <span style={{ fontSize: 12.5, fontWeight: 700 }}>
            در حال مشاهدهٔ نسخهٔ {Number(snapshotView.version).toLocaleString('fa-IR')} - فقط مشاهده
          </span>
          <button type="button" onClick={exitVersionView} style={{ padding: '6px 11px', borderRadius: 8,
            cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#0b1220', whiteSpace: 'nowrap',
            background: 'linear-gradient(135deg,#fcd34d,#f59e0b)', border: 'none', fontFamily: "'Vazirmatn', sans-serif" }}>
            بازگشت به نسخهٔ کاری
          </button>
        </div>
      )}
      {zoomGated && (
        <div style={{ position: 'absolute', top: isMobile ? 112 : 64, left: '50%', transform: 'translateX(-50%)',
          zIndex: 8, direction: 'rtl', fontFamily: "'Vazirmatn', sans-serif", padding: '8px 14px', borderRadius: 12,
          maxWidth: 'min(92vw, 440px)', color: '#ccfbf1', fontSize: 12.5, fontWeight: 700,
          background: 'linear-gradient(135deg, rgba(6,78,59,0.95), rgba(15,118,110,0.93))',
          border: '1px solid rgba(45,212,191,0.4)', boxShadow: '0 10px 30px rgba(2,6,23,0.5)' }}>
          برای نمایش ساختمان‌ها و معابر بیشتر زوم کنید
        </div>
      )}
      {!isMobile && prunedNotice}
      <DesignLayer map={map} result={layerResult} detailed={detailed} onHover={onHover} onSelect={onSelect}
        editMode={editMode} onElementMove={onElementMove} onElementSelect={onElementSelectEdit} />
      <MapRotateControl map={map} />
      <MapTypeToggle value={mapType} onChange={setMapType} />
      <MapHelpCluster onShortcuts={() => setKbOpen(true)} onLegend={() => setHelpOpen(true)} />
      <div data-testid="design-control-panel" style={isMobile
        ? { position: 'absolute', top: 58, insetInlineStart: 8, insetInlineEnd: 8, zIndex: 7, direction: 'rtl',
            display: 'flex', flexDirection: 'column', gap: 10, fontFamily: "'Vazirmatn', sans-serif",
            maxHeight: 'calc(100vh - 70px)', overflowY: 'auto', padding: 12, borderRadius: 14,
            background: 'linear-gradient(135deg, rgba(15,23,42,0.97), rgba(30,41,59,0.95))',
            border: '1px solid rgba(148,163,184,0.22)', boxShadow: '0 12px 34px rgba(2,6,23,0.6)' }
        : { position: 'absolute', top: 14, insetInlineEnd: 14, zIndex: 7, direction: 'rtl',
            display: 'flex', flexDirection: 'column', gap: 10, width: 290, fontFamily: "'Vazirmatn', sans-serif",
            maxHeight: 'calc(100vh - 28px)', overflowY: 'auto', scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(148,163,184,0.55) transparent', paddingInlineStart: 6,
            scrollbarGutter: 'stable' }}>
        <CarrierSwitcher variant="dark" />
        {/* Mobile: a header row whose chevron collapses the panel so the map
            underneath stays reachable for drawing / seed placement. */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#f1f5f9' }}>
              {projectId ? 'طراحیِ شبکه' : 'محدوده‌ی طراحی'}
            </div>
            <button type="button" onClick={() => setPanelCollapsed((v) => !v)}
              aria-label={panelCollapsed ? 'باز کردن پنل' : 'جمع کردن پنل'}
              style={{ width: 32, height: 28, borderRadius: 8, cursor: 'pointer', color: '#cbd5e1', fontSize: 13,
                background: 'rgba(51,65,85,0.7)', border: '1px solid rgba(148,163,184,0.32)' }}>
              {panelCollapsed ? '▾' : '▴'}
            </button>
          </div>
        )}
        <BuildingCandidateReview map={map} canReview={canReviewCandidates}
          blocked={drawMode || seedActive || editMode || running || panelBusy || !!snapshotView || kbOpen || helpOpen || historyOpen}
          onAccepted={onCandidateAccepted} />
        {candidateDirty && <div className="design-candidate-dirty" role="status">
          ساختمان‌های پایه تغییر کرده‌اند؛ برای به‌روز شدن طرح، طراحی را دوباره اجرا کنید.
        </div>}
        {(!isMobile || !panelCollapsed) && (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => navigate('/operator')} style={btn('ghost')}>‹ بازگشت</button>
              <button type="button" onClick={newProjectReset} style={btn('ghost', 1)}>پروژه‌ی جدید</button>
            </div>
            <button type="button" onClick={() => setHistoryOpen(true)} style={btn('ghost', 1)}>تاریخچهٔ طرح‌ها</button>
            <div aria-label="نمایش ساختمان‌ها" style={{ ...HINT, background: '#0f172a' }}>
              <div style={{ ...FIELD_LABEL, marginBottom: 4 }}>ساختمان‌ها</div>
              <label className="design-building-outline-switch">
                <input type="checkbox" role="switch" checked={outlineOnly}
                  onChange={(e) => pickOutlineOnly(e.target.checked)} aria-describedby="building-outline-help" />
                فقط خط دور
              </label>
              <div id="building-outline-help" style={{ color: '#cbd5e1', marginBottom: 10 }}>
                بدون رنگ داخل؛ تصویر زیر ساختمان‌ها دیده می‌شود.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', color: '#f1f5f9' }}>
                {[[BUILDING_COLORS.context, 'زمینه / بدون سرویس'], [BUILDING_COLORS.served, 'سرویس‌گرفته'],
                  [BUILDING_COLORS.selected, 'انتخاب‌شده']].map(([color, label]) => (
                  <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span aria-hidden="true" style={{ width: 16, borderTop: `2px solid ${color}` }} />{label}
                  </span>
                ))}
              </div>
            </div>
            {isMobile && prunedNotice}
            {!projectId && (
              <>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setDrawMode((v) => !v)} style={btn(drawMode ? 'primary' : 'ghost', 1)}>
                    {drawMode ? 'لغو رسم' : 'رسم محدوده'}
                  </button>
                  <button type="button" onClick={registerRegion} disabled={!canCreate}
                    aria-describedby={!profiles.length ? "design-rules-missing" : undefined}
                    style={{ ...btn('primary', 1), opacity: canCreate ? 1 : 0.6, cursor: canCreate ? 'pointer' : 'not-allowed' }}>ثبت محدوده</button>
                </div>
                {!profiles.length ? <div id="design-rules-missing">
                  {profilesError ? <div role="alert" style={{ color: '#fecaca', lineHeight: 1.8 }}>
                    <p>{profilesError}</p>
                    <button type="button" onClick={loadProfiles} style={btn('ghost')}>تلاش دوباره</button>
                  </div> : profilesLoaded ? <CarrierRulesMissing domain="design" carrier={carrier} canEdit={['admin', 'super_admin'].includes(profile?.role)} variant="dark" />
                    : <p role="status">در حال دریافت پیش‌تنظیم‌ها…</p>}
                </div> : <>
                <div style={{ ...HINT, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <SelectField id="operator-profile" label="پیش‌تنظیم طراحی" value={profileId}
                    onChange={pickProfile}
                    options={profiles.length ? [...(!profileId ? [['', 'پیش‌تنظیم را انتخاب کنید', true]] : []),
                      ...profiles.map((p) => [p.id, toFa(`${p.name} (نسخهٔ ${p.version.version_no})${p.is_default ? ' (پیش‌فرض)' : ''}`)])]
                      : [['', profilesLoaded ? 'پیش‌تنظیم منتشرشده در دسترس نیست' : 'در حال دریافت پیش‌تنظیم‌ها…', true]]}
                    help="مقدارهای زیر از این پیش‌تنظیم پر می‌شوند و برای همین طرح قابل تغییرند." />
                  {profileError && <div role="alert" style={{ color: '#fecaca', lineHeight: 1.7 }}>{profileError}</div>}
                </div>
                {PARAM_FIELDS.map(([key, label, options]) => {
                  const id = PARAM_IDS[key];
                  const layer = key === 'metro_cable_cores' ? 'metro' : key === 'odc_cable_cores' ? 'odc' : null;
                  const duct = layer && designParams[`${layer}_microduct_type`];
                  const note = layer ? (duct === '8x5'
                    ? 'با میکروداکت ۸/۵ فقط کابل ۴۸ کر مجاز است؛ برای ۷۲ یا ۹۶ کر میکروداکت ۱۴/۱۰ را انتخاب کن.'
                    : 'با میکروداکت ۱۴/۱۰ کابل ۷۲ یا ۹۶ کر مجاز است.')
                    : key === 'fat_per_port' ? <>کوچک‌ترین ODC: <bdi dir="rtl">۴ پورت × {faNum(odcSplitter)} = {faNum(4 * odcSplitter)} FAT</bdi></>
                    : key === 'mfat_splitter' && !mfatEnable ? 'در حالت «تمام ODC» استفاده نمی‌شود.' : null;
                  const help = key === 'mfat_splitter' ? (mfatEnable
                    ? `روی همهٔ پورت‌های MFAT: پورت ۱ برای ${faNum(mfatSplitter)} مشترک مستقیم و هر پورت دیگر برای ${faNum(mfatSplitter)} FAT.` : null)
                    : key === 'fat_cable_cores'
                      ? `۲ کر برای هر FAT. با سقف ۲ کابل در هر میکروداکت، هر شاخهٔ توزیع حداکثر ${faNum(designParams.fat_cable_cores)} FAT می‌برد.`
                      : PARAM_HELP[key];
                  return <div key={key} style={{ ...HINT, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {options ? <SelectField id={id} label={label}
                      value={key === 'mfat_enable' ? (mfatEnable ? 'mixed' : 'all_odc') : designParams[key]}
                      onChange={(value) => setParam(key, key === 'mfat_enable' ? value === 'mixed' : value)}
                      options={layer ? options.map(([v, t]) => [v, t, !FEEDER_CABLES_BY_DUCT[duct].includes(v)]) : options}
                      disabled={key === 'mfat_splitter' && !mfatEnable} help={help} note={note} />
                      : <>
                        <label htmlFor={id} style={FIELD_LABEL}>{label}</label>
                        <input id={id} type="number" min={RADIUS.min} max={RADIUS.max} step={RADIUS.step}
                          value={designParams[key]} onChange={(e) => setParam(key, Number(e.target.value))}
                          aria-describedby={`${id}-help`} style={FIELD} />
                        <div id={`${id}-help`} style={FIELD_HELP}>{help}</div>
                      </>}
                    {key === 'fat_capacity' && <>
                      <div data-testid="split-per-port" style={{ color: '#e2e8f0', fontWeight: 700 }}>
                        {/* Isolate each Latin label with its product; keep digits out of the label's LTR run. */}
                        مشترک به ازای هر پورت OLT: <bdi dir="rtl">ODC <bdi dir="rtl">{faNum(odcSplitter)}×{faNum(fatSplitter)} = {faNum(odcSplitter * fatSplitter)}</bdi></bdi>
                        {mfatEnable && <> · <bdi dir="rtl">MFAT <bdi dir="rtl">{faNum(mfatSplitter)}×{faNum(fatSplitter)} = {faNum(mfatSplitter * fatSplitter)}</bdi></bdi>
                          <div>MFAT دوپورتی {faNum(mfatSplitter + mfatSplitter * fatSplitter)} و چهارپورتی {faNum(mfatSplitter + 3 * mfatSplitter * fatSplitter)} مشترک</div>
                        </>}
                      </div>
                      {/* Keep the live region mounted so changes are announced. */}
                      <div role="status" style={maximumSplit > MAX_COMMON_SPLIT
                        ? { fontWeight: 700, color: '#fcd34d', padding: '7px 10px', borderRadius: 9,
                            background: 'rgba(180,83,9,0.22)', border: '1px solid rgba(251,191,36,0.4)' }
                        : undefined}>
                        {maximumSplit > MAX_COMMON_SPLIT && <>
                          تقسیم ۱:{faNum(maximumSplit)} از سقف رایج GPON/XGS-PON (۱:۲۵۶) فراتر است؛
                          طرح اجرا می‌شود ولی بودجهٔ نوری هر FAT احتمالاً کم می‌آید.
                        </>}
                      </div>
                    </>}
                  </div>;
                })}
                </>}
                <div style={HINT}>
                  {drawMode
                    ? 'روی نقشه دو گوشه‌ی محدوده را کلیک کن.'
                    : Array.isArray(region) && region.length >= 3
                      ? 'گوشه‌های محدوده را تنظیم کن (یا کلِ آن را جابه‌جا کن)، سپس «ثبت محدوده» را بزن.'
                      : 'نقشه را روی محدوده ببر و «رسم محدوده» را بزن - یا برای کلِ نمای فعلی مستقیم «ثبت محدوده».'}
                </div>
              </>
            )}
            {projectId && !snapshotView && (editMode ? (
              <DesignEditToolbar editType={editType} onArmType={setEditType}
                onApplyRerun={applyRerun} onSaveManual={saveManualEdit} onCancel={cancelEdit}
                busy={editBusy} msg={editMsg} />
            ) : (
              <>
                {needsRecompute && (
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#fcd34d', padding: '7px 10px', borderRadius: 9,
                    background: 'rgba(180,83,9,0.22)', border: '1px solid rgba(251,191,36,0.4)', lineHeight: 1.6 }}>
                    این طرح ویرایشِ دستی دارد - نیازمندِ محاسبهٔ مجدد
                  </div>
                )}
                <div style={HINT}>سایت/ODC/FAT را روی نقشه بگذار، سپس «اجرای طراحی». بعد از هر افزودن، دوباره اجرا کن.</div>
                <button type="button" onClick={() => setSeedActive((v) => !v)} style={btn(seedActive ? 'primary' : 'ghost')}>
                  {seedActive ? 'بستن ابزار گذاشتن' : 'ابزار گذاشتن سایت/ODC/FAT'}
                </button>
                {seedActive && <SeedTypePicker type={seedType} onType={setSeedType} />}
                <DesignPanel projectId={projectId} projectName={projectName} profileName={profileName} profileVersionNo={profileVersionNo} carrier={carrier} canEdit={['admin', 'super_admin'].includes(profile?.role)} mode={mode} onModeChange={setMode} onResult={onRun} onBusyChange={setPanelBusy} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={pinCabinets} style={btn('ghost', 1)}>تثبیت کابینت‌ها</button>
                  <button type="button" onClick={resetSeeds} style={btn('ghost', 1)}>بازنشانی seedها</button>
                </div>
                {result && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={saveVersion} disabled={versionBusy || panelBusy}
                      style={{ ...btn('ghost', 1), opacity: panelBusy ? 0.55 : 1 }}>
                      {versionBusy ? 'در حال ذخیره…' : 'ذخیرهٔ نسخه'}
                    </button>
                    <button type="button" onClick={enterEdit} disabled={panelBusy}
                      style={{ ...btn('ghost', 1), opacity: panelBusy ? 0.55 : 1 }}>حالتِ ویرایش</button>
                  </div>
                )}
              </>
            ))}
            {versionMsg && (
              <div role={versionMsg.ok ? 'status' : 'alert'} style={{ fontSize: 11.5, fontWeight: 700, color: versionMsg.ok ? '#86efac' : '#fca5a5' }}>
                {versionMsg.t}
              </div>
            )}
          </>
        )}
      </div>
      <DesignDetailOverlay active={detailed} hasResult={!!result} projectId={projectId}
        onToggle={toggleDetailed} info={lockedInfo || hoverInfo} locked={!!lockedInfo} onClose={closeCard}
        editMode={editMode} onDelete={onDeleteElement} />
      <DesignHistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)}
        onSelect={openHistoryProject} onSelectVersion={openVersionView} isMobile={isMobile} />
      {kbOpen && <KeyboardShortcutsModal onClose={() => setKbOpen(false)} />}
      {helpOpen && <DesignHelpModal onClose={() => setHelpOpen(false)} summary={result && result.summary} />}
      <DesignSpinner active={running} />
    </>
  );
}

export default function DesignPage() {
  if (!KEY) return <div style={{ padding: 20, direction: 'rtl' }}>کلید Google Maps تنظیم نشده.</div>;
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <APIProvider apiKey={KEY} language="fa" region="IR">
        <InnerDesignPage />
      </APIProvider>
    </div>
  );
}
